import type { Ref } from 'react'
import { money } from '../../lib/format'
import { defaultTerms } from '../../lib/terms'
import type { WorkOrder } from '../../lib/api'
import { DocHead } from '../../components/ui'

/**
 * 發包單的列印版面本體（不含工具列），供單張列印與批次列印共用。
 * 版面依《部落格文章委外發包單_範本.docx》
 */
const CN = '一二三四五六七八九'

export default function WorkOrderDoc({ w, docRef }: {
  w: WorkOrder
  /** 下載 PDF 要拿這一塊去渲染 */
  docRef?: Ref<HTMLDivElement>
}) {
  const tax = Math.round(w.total_amount * 0.05)
  /**
   * 章節編號依實際渲染的區塊遞增。
   * 「文章需求明細」沒有資料時不會出現，寫死編號會跳號（一、二、四…）。
   */
  let sectionNo = 0
  const sec = (title: string) => `${CN[sectionNo++]}、${title}`
  /**
   * 個人寫手沒有發票，款項是實際支付、不另計稅，
   * 所以合計區只印一列總計，不出現稅額與含稅總計。
   * 廠商（會開立發票）維持原本的三列。
   */
  const actualPay = w.tax_mode === '實際支付'

  /** 抬頭前綴看發包類型：對外發包與內部自寫是兩件事，文件上要分得出來 */
  const lead = w.dispatch_type === '內包' ? '文案內部外包' : '文案委外'

  return (
    <div className="print-doc" ref={docRef}>
      <DocHead lead={lead} title="發包單" />

      <h2>{sec('發包基本資料')}</h2>
      <table>
        <tbody>
          <tr>
            <th style={{ width: '14%' }}>發包單號</th><td style={{ width: '19%' }}>{w.code}</td>
            <th style={{ width: '14%' }}>承包人</th><td style={{ width: '19%' }}>{w.writer_name} {w.writer_name_en}</td>
            {/* 舊資料沒存過聯絡方式，用寫手主檔的 Email 遞補 */}
            <th style={{ width: '14%' }}>聯絡方式</th><td>{w.issuer_contact || w.writer_email}</td>
          </tr>
          <tr>
            <th>發包單位</th><td>{w.dept}</td>
            <th>發包人</th><td>{w.issuer_name}</td>
            <th>案件負責人</th><td>{w.owner_name}</td>
          </tr>
          <tr>
            <th>發包日期</th><td>{w.issued_on}</td>
            <th>承包確認日</th><td>{w.confirmed_on}</td>
            <th>約定交件日</th><td>{w.due_on}</td>
          </tr>
          <tr>
            <th>語系</th><td>{w.lang}</td>
            <th>幣別／稅別</th><td>{w.tax_mode}</td>
            <th>費率等級</th><td>{w.rate_level}</td>
          </tr>
          <tr>
            <th>稿件交付連結</th>
            <td colSpan={5}>{w.deliver_link}</td>
          </tr>
        </tbody>
      </table>

      <h2>{sec('委外項目與金額')}</h2>
      <table>
        <thead>
          <tr>
            <th style={{ width: 34 }}>#</th>
            <th>客戶／專案</th>
            <th style={{ width: 110, whiteSpace: 'nowrap' }}>合約編號</th>
            <th>服務項目</th>
            <th className="num" style={{ width: 60 }}>件數</th>
            <th className="num" style={{ width: 80 }}>單價 (NT$)</th>
            <th className="num" style={{ width: 90 }}>小計 (NT$)</th>
          </tr>
        </thead>
        <tbody>
          {w.items.map((it, i) => (
            <tr key={it.id ?? i}>
              <td>{i + 1}</td>
              <td>{it.client_name}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{it.contract_code}</td>
              <td>{it.job_type_name ?? it.service}</td>
              <td className="num">{it.qty}</td>
              <td className="num">{money(it.unit_price)}</td>
              <td className="num">{money(it.subtotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {actualPay ? (
            // 個人：不計稅也不重複列件數，只在「單價」欄的位置標示總金額
            <tr>
              <th colSpan={5}></th>
              <th className="num">總金額</th>
              <td className="num">{money(w.total_amount)}</td>
            </tr>
          ) : (
            <>
              {/* 三列都是「標籤落在單價欄、金額落在小計欄」，讀起來是同一段結帳。
                  合計列不再重複件數——明細每列都有件數，合計再列一次只是雜訊 */}
              <tr>
                <th colSpan={6}>合計（未稅）</th>
                <td className="num">{money(w.total_amount)}</td>
              </tr>
              <tr>
                <th colSpan={6}>稅額 5%（{w.tax_mode}）</th>
                <td className="num">{money(tax)}</td>
              </tr>
              <tr>
                <th colSpan={6}>總計（含稅）</th>
                <td className="num">{money(w.total_amount + tax)}</td>
              </tr>
            </>
          )}
        </tfoot>
      </table>

      {w.articles.length > 0 && (
        <>
          <h2>{sec('文章需求明細')}</h2>
          <table>
            <thead>
              <tr>
                <th style={{ width: 34 }}>序號</th>
                <th>客戶</th>
                <th>文章主題（暫定標題）</th>
                <th>目標關鍵字</th>
                <th style={{ width: 88 }}>本篇交稿日</th>
              </tr>
            </thead>
            <tbody>
              {w.articles.map((a, i) => (
                <tr key={a.id ?? i}>
                  <td>{i + 1}</td>
                  <td>{(a as any).client_name}</td>
                  <td>{a.title}</td>
                  <td>{a.keyword}</td>
                  <td>{a.due_on}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* 備註是這一單的個別說明，不是合約條款，所以獨立成一節放在注意事項之前，
          不再接在條款後面當第 5 條。沒填就整節不印，避免留一塊空白 */}
      {w.note && (
        <>
          <h2>{sec('備註')}</h2>
          <div className="terms" style={{ whiteSpace: 'pre-wrap' }}>{w.note}</div>
        </>
      )}

      {/* 注意事項改由發包單自己存；留白就沿用該發包類型的預設條款 */}
      <h2>{sec('注意事項')}</h2>
      <div className="terms" style={{ whiteSpace: 'pre-wrap' }}>
        {w.terms?.trim() || defaultTerms(w.dispatch_type)}
      </div>

      <h2>{sec('簽核')}</h2>
      <table className="sign-table">
        <tbody>
          <tr>
            <th style={{ width: '34%' }}>承包人</th>
            <th style={{ width: '33%' }}>發包人</th>
            <th>直屬主管</th>
          </tr>
        </tbody>
        <tbody className="sign-row">
          <tr><td></td><td></td><td></td></tr>
        </tbody>
      </table>

      <p className="foot-note">本發包單一式二份，甲乙雙方各執一份。</p>
    </div>
  )
}
